import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";
import { projects } from "../data/projects-bilingual";

export default function Projects() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedProject, setExpandedProject] = useState<string | null>(null);

  // Category mapping
  const categories = [
    { key: "all", label: t('projects.categories.all') },
    { key: "AI/MLシステム", label: language === 'ja' ? 'AI/MLシステム' : 'AI/ML Systems' },
    { key: "AI/MLアプリケーション", label: language === 'ja' ? 'AI/MLアプリケーション' : 'AI/ML Applications' },
    { key: "研究プロジェクト", label: language === 'ja' ? '研究プロジェクト' : 'Research Projects' },
    { key: "AIガバナンス", label: language === 'ja' ? 'AIガバナンス' : 'AI Governance' },
    { key: "環境AI", label: language === 'ja' ? '環境AI' : 'Environmental AI' }
  ];

  const filteredProjects = selectedCategory === "all"
    ? projects
    : projects.filter(p => p.category === selectedCategory);

  const renderProjectCard = (project: typeof projects[0]) => {
    const content = language === 'ja' ? project.ja : project.en;
    const isExpanded = expandedProject === project.id;

    return (
      <motion.div
        key={project.id}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={`card card-hover ${project.featured ? 'border-l-4 border-accent' : ''}`}
      >
        {/* Title */}
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-bold text-white flex-1">{content.title}</h3>
          {project.featured && (
            <span className="ml-3 bg-accent/20 text-accent border border-accent/30 px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
              {t('projects.labels.featured')}
            </span>
          )}
        </div>

        {/* Problem */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-accent mb-2">{t('projects.labels.problem')}</h4>
          <p className="text-sm text-muted-light leading-relaxed">{content.problem}</p>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4 pb-4"
          >
            {/* Role */}
            <div>
              <h4 className="text-sm font-semibold text-accent mb-2">{t('projects.labels.role')}</h4>
              <p className="text-sm text-muted-light leading-relaxed">{content.role}</p>
            </div>

            {/* Challenges */}
            <div>
              <h4 className="text-sm font-semibold text-accent mb-2">{t('projects.labels.challenges')}</h4>
              <p className="text-sm text-muted-light leading-relaxed">{content.challenges}</p>
            </div>

            {/* Outcome */}
            <div>
              <h4 className="text-sm font-semibold text-accent mb-2">{t('projects.labels.outcome')}</h4>
              <p className="text-sm text-muted-light leading-relaxed">{content.outcome}</p>
              {content.metrics && (
                <p className="text-sm text-accent font-medium mt-2">📊 {content.metrics}</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Tech Stack */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-muted mb-2">{t('projects.labels.tools')}</h4>
          <div className="flex flex-wrap gap-2">
            {project.tools.slice(0, isExpanded ? project.tools.length : 6).map((tool) => (
              <span key={tool} className="text-xs bg-tertiary/30 text-muted-light px-2 py-1 rounded-md">
                {tool}
              </span>
            ))}
            {!isExpanded && project.tools.length > 6 && (
              <span className="text-xs text-accent">+{project.tools.length - 6} more</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={() => setExpandedProject(isExpanded ? null : project.id)}
            className="text-accent hover:text-accent-light transition-colors text-sm font-medium"
          >
            {isExpanded ? (language === 'ja' ? '📖 閉じる' : '📖 Show Less') : t('common.readMore')}
          </button>
          
          {project.link && (
            <a 
              href={project.link} 
              target="_blank" 
              rel="noreferrer" 
              className="text-accent hover:text-accent-light transition-colors text-sm font-medium"
            >
              {t('projects.labels.viewDemo')}
            </a>
          )}
          
          <a 
            href={project.repo} 
            target="_blank" 
            rel="noreferrer" 
            className="text-accent hover:text-accent-light transition-colors text-sm font-medium"
          >
            {t('projects.labels.viewRepo')}
          </a>

          {project.downloadFile && (
            <a 
              href={project.downloadFile} 
              download
              className="text-accent hover:text-accent-light transition-colors text-sm font-medium"
            >
              {t('projects.labels.download')}
            </a>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-secondary to-primary">
      <div className="section-padding">
        <div className="container-width">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('projects.title')}
            </h1>
            <p className="text-xl text-muted-light max-w-3xl mx-auto">
              {language === 'ja' 
                ? 'AI/ML、BCI、人間中心のインテリジェントシステム構築プロジェクト' 
                : 'Building intelligent, ethical, and human-centric AI systems'}
            </p>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12"
          >
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => setSelectedCategory(category.key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedCategory === category.key
                      ? 'bg-accent text-primary shadow-lg shadow-accent/30'
                      : 'bg-tertiary/30 text-muted-light hover:bg-tertiary/50'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Projects Grid */}
          <div className="space-y-6">
            {filteredProjects.map(renderProjectCard)}
          </div>

          {/* GitHub CTA */}
          <div className="text-center mt-16">
            <div className="card max-w-2xl mx-auto text-center">
              <h3 className="text-xl font-bold text-accent mb-4">
                {language === 'ja' ? '🚀 GitHubでさらに探索' : '🚀 Explore More on GitHub'}
              </h3>
              <p className="text-muted-light mb-6">
                {language === 'ja' 
                  ? 'すべてのプロジェクト、コントリビューション、進行中の開発作業を発見'
                  : 'Discover all projects, contributions, and ongoing development work'}
              </p>
              <a 
                href="https://github.com/AB2511" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary inline-block"
              >
                {language === 'ja' ? 'GitHub プロフィールを見る' : 'View GitHub Profile'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
